import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SalarySubNav from './SalarySubNav';
import { apiGet } from './api';
import { fmtCurrency, fmtDate, periodRowDisplayStatus } from './formatters';
import type { SalaryPeriod, RemittanceRow } from './types';
import './SalaryModule.css';

const SalaryDashboard: React.FC = () => {
  const { sessionToken } = useAuth();
  const [periods, setPeriods] = useState<SalaryPeriod[]>([]);
  const [remittances, setRemittances] = useState<RemittanceRow[]>([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([
          apiGet<{ periods: SalaryPeriod[] }>('/periods', sessionToken),
          apiGet<{ remittances: RemittanceRow[] }>('/remittances', sessionToken),
        ]);
        setPeriods(p.periods);
        setRemittances(r.remittances);
      } catch (e: any) {
        setErr(e.message);
      }
    })();
  }, [sessionToken]);

  const now = new Date().toISOString().slice(0, 10);
  // The period that contains today; else the most recent one that has started.
  const current =
    periods.find((p) => p.start_date <= now && p.end_date >= now) ||
    [...periods].reverse().find((p) => p.start_date <= now) ||
    periods[periods.length - 1];

  const ytdGross = periods.reduce((sum, p) => sum + Number(p.total_gross || 0), 0);
  const ytdNet = periods.reduce((sum, p) => sum + Number(p.total_net || 0), 0);
  const pendingRem = remittances.filter((r) => r.status === 'pending');

  const recent = [...periods].reverse().slice(0, 4);

  return (
    <div className="salary-module">
      <h2>Salary</h2>
      <SalarySubNav />

      {err && <div className="error-banner">{err}</div>}

      {current && (
        <div className="sal-hero">
          <div className="toprow">
            <div>
              <div className="l">Current pay period</div>
              <div className="big num">{fmtCurrency(current.total_net || 0)}</div>
              <div className="rng">{fmtDate(current.start_date)} → {fmtDate(current.end_date)} · net</div>
            </div>
            <span className={`sal-chip sc-${periodRowDisplayStatus(current)}`}>{periodRowDisplayStatus(current)}</span>
          </div>
          <div className="split">
            <div><div className="l">Employees</div><div className="v num">{current.entry_count || 0}</div></div>
            <div><div className="l">Gross</div><div className="v num">{fmtCurrency(current.total_gross || 0)}</div></div>
            <div><div className="l">Payout</div><div className="v num" style={{ fontSize: '0.9rem' }}>{fmtDate(current.payout_date)}</div></div>
          </div>
        </div>
      )}

      <div className="sal-kpi">
        <div className="sal-k"><div className="l">YTD gross (all periods)</div><div className="v num">{fmtCurrency(ytdGross)}</div></div>
        <div className="sal-k"><div className="l">YTD net paid</div><div className="v num">{fmtCurrency(ytdNet)}</div></div>
      </div>

      {pendingRem.length > 0 && (
        <>
          <div className="sal-label">Needs your attention</div>
          <Link to="/salary/remittances" className="sal-row">
            <div className="sal-av" style={{ background: 'var(--cs-bad-soft)', color: 'var(--cs-bad)' }}>🏛️</div>
            <div className="body">
              <b>CRA remittance pending</b>
              <div className="m">{pendingRem.length} month{pendingRem.length === 1 ? '' : 's'} · oldest {pendingRem[pendingRem.length - 1].period_month}</div>
            </div>
            <span className="chev" aria-hidden>›</span>
          </Link>
        </>
      )}

      <div className="sal-label">Recent periods</div>
      {recent.map((p) => (
        <Link to={`/salary/periods/${p.id}`} className="sal-row" key={p.id}>
          <div className="body">
            <div className="top">
              <b>{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</b>
              <span className={`sal-chip sc-${periodRowDisplayStatus(p)}`}>{periodRowDisplayStatus(p)}</span>
            </div>
            <div className="m">{p.entry_count || 0} employees · payout {fmtDate(p.payout_date)}</div>
          </div>
          <div className="right">
            <div className="amt num">{fmtCurrency(p.total_net || 0)}</div>
            <div className="cap">net</div>
          </div>
          <span className="chev" aria-hidden>›</span>
        </Link>
      ))}
      {periods.length === 0 && (
        <p style={{ textAlign: 'center', color: 'var(--cs-ink-2)', padding: '1.5rem' }}>
          No periods yet. <Link to="/salary/periods">Create the year’s periods</Link>.
        </p>
      )}

      {periods.length > 0 && (
        <Link to="/salary/periods" className="salary-btn secondary full" style={{ marginTop: 6 }}>
          Show all pay periods
        </Link>
      )}
    </div>
  );
};

export default SalaryDashboard;
